import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import GoogleMaps from './GoogleMaps';
import { MapPin, Phone, Mail, Clock, Send, ParkingCircle } from 'lucide-react';
import { restaurantInfo } from '../data/restaurantInfo';

const ContactPage = () => {
  const [isVisible, setIsVisible] = useState(false);
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Contact form submitted:', formData);
    alert('Thank you for your message! We\'ll get back to you soon.');
    setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-[#F5F3EF]">
      {/* Hero Section - Compact */}
      <section className="pb-12 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_multilingual-menu-2/artifacts/y2iztjgb_FB_IMG_1759417832988.jpg"
            alt="Contact Us Background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, rgba(47, 47, 47, 0.8), rgba(245, 243, 239, 0.98))' }} />
        </div>
        
        <div className="max-w-7xl mx-auto px-6 py-16 text-center relative z-10">
          <span className="text-[#C97A2B] text-xs tracking-[0.3em] uppercase">Get In Touch</span>
          <h1 className={`text-5xl md:text-6xl mt-4 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} style={{ fontFamily: 'DM Serif Display, serif', color: '#ffffff' }}>
            Contact <span className="italic text-[#C97A2B]">Us</span>
          </h1>
        </div>
      </section>

      {/* Contact Information & Form - Streamlined */}
      <section className="py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Information - Compact Cards */}
            <div className="space-y-6">
              {/* Address & Parking */}
              <div className="bg-white rounded-xl p-6 border border-[#2F2F2F]/10 shadow-sm">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-4">
                    <MapPin className="w-6 h-6 text-[#C97A2B] flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-[#2F2F2F] mb-1">Address</h3>
                      <p className="text-[#6B705C] text-sm">
                        {restaurantInfo.address.street}<br />
                        {restaurantInfo.address.postalCode} {restaurantInfo.address.city}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <ParkingCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-[#2F2F2F] mb-1">Parking</h3>
                      <p className="text-[#6B705C] text-sm">{restaurantInfo.parking.type}</p>
                      <p className="text-green-600 text-xs mt-1">{restaurantInfo.parking.tips}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Phone & Email */}
              <div className="bg-white rounded-xl p-6 border border-[#2F2F2F]/10 shadow-sm">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-4">
                    <Phone className="w-6 h-6 text-[#C97A2B] flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-[#2F2F2F] mb-1">Phone</h3>
                      <a href="tel:0403779200" className="text-[#C97A2B] hover:underline text-lg font-medium">
                        040 377 9200
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Mail className="w-6 h-6 text-[#C97A2B] flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-[#2F2F2F] mb-1">Email</h3>
                      <a href={`mailto:${restaurantInfo.contact.email}`} className="text-[#6B705C] hover:text-[#C97A2B] text-sm">
                        {restaurantInfo.contact.email}
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Hours */}
              <div className="bg-white rounded-xl p-6 border border-[#2F2F2F]/10 shadow-sm">
                <div className="flex items-start gap-4">
                  <Clock className="w-6 h-6 text-[#C97A2B] flex-shrink-0 mt-1" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-[#2F2F2F] mb-3">Opening Hours</h3>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="text-[#6B705C]">
                        <p>Mon - Thu: 11:00 - 21:00</p>
                        <p>Friday: 11:00 - 22:00</p>
                      </div>
                      <div className="text-[#6B705C]">
                        <p>Saturday: 12:00 - 22:00</p>
                        <p className="text-[#C97A2B]">Sunday: 12:00 - 20:00 (À la carte)</p>
                      </div>
                    </div>
                    <p className="text-[#2F2F2F] text-sm mt-3 pt-3 border-t border-[#2F2F2F]/10 font-medium">
                      Lunch: Mon-Fri 11-15, Sat 12-16
                    </p>
                  </div>
                </div>
              </div>

              {/* Google Maps */}
              <div className="rounded-xl overflow-hidden border border-[#2F2F2F]/10 shadow-sm">
                <GoogleMaps />
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white rounded-xl p-8 border border-[#2F2F2F]/10 shadow-sm h-fit">
              <h3 className="text-2xl text-[#2F2F2F] mb-6" style={{ fontFamily: 'DM Serif Display, serif' }}>
                Send us a <span className="italic text-[#C97A2B]">Message</span>
              </h3>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#2F2F2F] text-sm font-medium mb-1">Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full bg-[#F5F3EF] border border-[#2F2F2F]/20 rounded-lg px-4 py-2.5 text-[#2F2F2F] focus:outline-none focus:border-[#C97A2B] transition-colors"
                      placeholder="Your name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-[#2F2F2F] text-sm font-medium mb-1">Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full bg-[#F5F3EF] border border-[#2F2F2F]/20 rounded-lg px-4 py-2.5 text-[#2F2F2F] focus:outline-none focus:border-[#C97A2B] transition-colors"
                      placeholder="your@email.com"
                      required
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[#2F2F2F] text-sm font-medium mb-1">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full bg-[#F5F3EF] border border-[#2F2F2F]/20 rounded-lg px-4 py-2.5 text-[#2F2F2F] focus:outline-none focus:border-[#C97A2B] transition-colors"
                      placeholder="+358 40 123 4567"
                    />
                  </div>
                  <div>
                    <label className="block text-[#2F2F2F] text-sm font-medium mb-1">Subject *</label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      className="w-full bg-[#F5F3EF] border border-[#2F2F2F]/20 rounded-lg px-4 py-2.5 text-[#2F2F2F] focus:outline-none focus:border-[#C97A2B] transition-colors"
                      required
                    >
                      <option value="">Select subject</option>
                      <option value="reservation">Reservation</option>
                      <option value="private-event">Private Events</option>
                      <option value="feedback">Feedback</option>
                      <option value="catering">Catering</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-[#2F2F2F] text-sm font-medium mb-1">Message *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    rows="4"
                    className="w-full bg-[#F5F3EF] border border-[#2F2F2F]/20 rounded-lg px-4 py-2.5 text-[#2F2F2F] focus:outline-none focus:border-[#C97A2B] transition-colors resize-none"
                    placeholder="How can we help?"
                    required
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full bg-[#2F2F2F] text-white font-medium py-3 rounded-lg transition-all duration-300 hover:bg-[#C97A2B] flex items-center justify-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  <span>SEND MESSAGE</span>
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - Compact */}
      <section className="py-16 bg-[#2F2F2F]">
        <div className="max-w-3xl mx-auto text-center px-6">
          <h2 className="text-3xl md:text-4xl text-white mb-4" style={{ fontFamily: 'DM Serif Display, serif' }}>
            Ready to <span className="italic text-[#C97A2B]">Dine</span> With Us?
          </h2>
          <p className="text-white/60 mb-6">
            Reserve your table today for an unforgettable experience.
          </p>
          <button 
            onClick={() => navigate('/combine-tables')}
            className="px-8 py-3 bg-[#C97A2B] text-black font-medium rounded-lg transition-all duration-300 hover:bg-white"
            data-testid="contact-reserve-btn"
          >
            MAKE RESERVATION
          </button>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;